/**
 * 종목 거래·배당 내역 카드 — StockDetailDrawer 전용 (쿼리 내장형)
 * 거래/배당(최근 3년)을 페치 후 해당 종목 필터 — TanStack 캐시 활용
 * userIds prop으로 화면의 사용자 필터 범위를 그대로 따름 (미전달 시 전체 사용자)
 * 거래 손익 = 현재가(차트 캐시) 대비 해당 거래가의 평가손익 (해외는 환율 API로 KRW 환산)
 */
import { Card, Table, Tag } from "antd";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { briefApi } from "src/api/brief";
import { marketApi } from "src/api/market";
import { stocksApi } from "src/api/stocks";
import { isOverseasStock, toKisSymbol } from "src/components/StockDetailDrawer";
import { AccountTag } from "src/lib/brokerage";
import { currency, formatWon } from "src/lib/format";
import { USERS } from "src/lib/users";
import type { DividendResponse, PortfolioTransaction } from "src/models/Portfolio";
import { useScreenStore } from "src/stores/screenStore";

/** userIds 미전달 시 전체 사용자 fallback (기존 동작 호환) */
const ALL_USERS = USERS.map((u) => u.id);

/** 통화별 가격 포맷 — KRW 정수 쉼표 / USD 소수 2자리 (거래내역 탭 셀 포맷 승계) */
const priceFmt = (v: number, usd: boolean) =>
  usd
    ? `$ ${Number(v).toLocaleString("ko-KR", { maximumFractionDigits: 2 })}`
    : `${Math.round(Number(v)).toLocaleString("ko-KR")} 원`;

interface StockHistoryCardProps {
  stockId: string;
  /** 드로어 열림 상태에서만 fetch */
  enabled?: boolean;
  /** 표시할 사용자 범위 — 미전달 시 전체 사용자 (기존 동작) */
  userIds?: string[];
}

const StockHistoryCard = ({ stockId, enabled = true, userIds }: StockHistoryCardProps) => {
  const screens = useScreenStore((s) => s.screens);
  const qc = useQueryClient();
  const overseas = isOverseasStock(stockId);
  const symbol = toKisSymbol(stockId);

  const scope = userIds ?? ALL_USERS;
  const txQ = useQuery({
    queryKey: ["stocks", "transactions", scope],
    queryFn: () => stocksApi.getTransactions(scope),
    enabled,
    staleTime: 5 * 60_000,
  });
  const divQ = useQuery({
    queryKey: ["dividends", "recent3y", scope],
    queryFn: () =>
      stocksApi.getDividends(
        dayjs().subtract(3, "year").format("YYYY-MM-DD"),
        dayjs().format("YYYY-MM-DD"),
        scope,
      ),
    enabled,
    staleTime: 10 * 60_000,
  });

  // 현재가 — 드로어 차트(기본 6M=130일) 캐시 우선, 없으면 백그라운드 페치 (KIS 일봉 마지막 종가)
  const CHART_KEY = ["market", "chart", symbol, 130, overseas] as const;
  const currentPrice = useMemo(() => {
    const cached = qc.getQueryData<{ candles?: { close: number }[] }>(CHART_KEY as any);
    const candles = cached?.candles;
    return candles && candles.length > 0 ? candles[candles.length - 1].close : null;
  }, [qc, CHART_KEY, txQ.dataUpdatedAt, divQ.dataUpdatedAt]);

  const priceQ = useQuery({
    queryKey: CHART_KEY,
    queryFn: () => marketApi.getChart(symbol, 130, overseas),
    enabled: enabled && !!symbol && currentPrice == null,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  const livePrice = currentPrice ?? (priceQ.data?.candles?.length ? priceQ.data.candles[priceQ.data.candles.length - 1].close : null);

  // 해외 종목 KRW 환산용 환율
  const rateQ = useQuery({
    queryKey: ["brief", "exchange-rate"],
    queryFn: () => briefApi.getExchangeRate(),
    enabled: enabled && overseas,
    staleTime: 10 * 60_000,
  });
  const fxRate = rateQ.data?.current_rate ?? null;

  const txns = useMemo(
    () =>
      (txQ.data || [])
        .filter((t) => t.stock_id === stockId)
        .sort((a, b) => b.transaction_date.localeCompare(a.transaction_date)),
    [txQ.data, stockId],
  );

  const dividends = useMemo(
    () =>
      (divQ.data || [])
        .filter((d) => d.stock_id === stockId)
        .sort((a, b) => b.payment_date.localeCompare(a.payment_date)),
    [divQ.data, stockId],
  );

  // 최근 12개월 배당 합계
  const yearAgo = dayjs().subtract(12, "month").format("YYYY-MM-DD");
  const recentDivTotal = dividends
    .filter((d) => d.payment_date.slice(0, 10) >= yearAgo)
    .reduce((s, d) => s + d.dividends_krw, 0);

  // 연 배당률(TTM) — 최근 12개월 고유 지급일의 주당배당 합 ÷ 현재가 (주당/주가라 통화 무관)
  const annualDps = useMemo(() => {
    const seen = new Set<string>();
    let sum = 0;
    for (const d of dividends) {
      const dt = d.payment_date.slice(0, 10);
      if (dt < yearAgo || seen.has(dt)) continue;
      seen.add(dt);
      sum += d.dividend_per_share;
    }
    return sum;
  }, [dividends, yearAgo]);
  const divYield = livePrice && annualDps > 0 ? (annualDps / livePrice) * 100 : null;

  return (
    <>
      {/* 거래내역 */}
      <Card size="small" title={`거래내역 (${txns.length}건)`} style={{ marginTop: 12 }}>
        <Table
          size="small"
          loading={txQ.isLoading}
          dataSource={txns}
          rowKey="transaction_id"
          pagination={txns.length > 10 ? { pageSize: 10, size: "small" } : false}
          columns={[
            {
              title: "일자", dataIndex: "transaction_date", width: 100,
              render: (v: string, r: PortfolioTransaction) => (
                <div>
                  <div style={{ fontSize: 12 }}>{v}</div>
                  {!screens.md && <AccountTag account={`${r.brokerage} ${r.account_type}`} />}
                </div>
              ),
            },
            {
              title: "구분", dataIndex: "transaction_type", width: 60,
              render: (v: string) => <Tag color={v === "BUY" ? "red" : "blue"}>{v === "BUY" ? "매수" : "매도"}</Tag>,
            },
            {
              title: "계좌", dataIndex: "brokerage", responsive: ["md"] as const, width: 120,
              render: (v: string, r: PortfolioTransaction) => <AccountTag brokerage={v} account={`${v} ${r.account_type}`} />,
            },
            { title: "가격", dataIndex: "price", align: "right" as const, width: 85, render: (v: number, r: PortfolioTransaction) => <span style={{ fontSize: 12 }}>{priceFmt(v, r.currency === "USD")}</span> },
            {
              title: "금액(KRW)", dataIndex: "amount_krw", align: "right" as const, width: 105,
              render: (v: number, r: PortfolioTransaction) => (
                <div>
                  <div>{currency(v)}</div>
                  <div style={{ fontSize: 11, color: "#999" }}>{r.quantity?.toLocaleString("ko-KR")}주</div>
                </div>
              ),
            },
            {
              title: "손익", key: "pnl", align: "right" as const, width: 125,
              render: (_: any, r: PortfolioTransaction) => {
                if (!livePrice) return <span style={{ color: "#999" }}>-</span>;
                const usd = r.currency === "USD";
                // BUY: 현재가 대비 평가손익 / SELL: 매도가 vs 현재가 (더 싸게 팔았으면 손해)
                const diff = r.transaction_type === "BUY" ? livePrice - r.price : r.price - livePrice;
                const rate = usd ? fxRate : 1;
                if (!rate) return <span style={{ color: "#999" }}>-</span>;
                const pnlKrw = diff * r.quantity * rate;
                const pct = r.price !== 0 ? (diff / r.price) * 100 : 0;
                return (
                  <span className={pnlKrw > 0 ? "fred" : pnlKrw < 0 ? "fblue" : ""} style={{ fontSize: 12 }}>
                    {pnlKrw > 0 ? "+" : ""}{formatWon(Math.round(pnlKrw))}{" "}
                    <span style={{ fontSize: 11 }}>({pct > 0 ? "+" : ""}{pct.toFixed(2)}%)</span>
                  </span>
                );
              },
            },
          ] as any}
        />
      </Card>

      {/* 배당내역 */}
      <Card
        size="small"
        title={`배당내역 (${dividends.length}건)`}
        extra={
          dividends.length > 0 ? (
            <span style={{ fontSize: 12, color: "#27ae60" }}>
              최근 1년 {formatWon(Math.round(recentDivTotal))}
              {divYield != null && <span style={{ color: "#999", margin: "0 2px" }}>·</span>}
              {divYield != null && <span style={{ fontWeight: 600 }}>연 {divYield.toFixed(2)}%</span>}
            </span>
          ) : undefined
        }
        style={{ marginTop: 12 }}
      >
        <Table
          size="small"
          loading={divQ.isLoading}
          dataSource={dividends}
          rowKey={(r: DividendResponse) => `${r.stock_id}-${r.payment_date}-${r.brokerage}-${r.account_type}-${r.user_id}-${r.dividends}`}
          pagination={dividends.length > 10 ? { pageSize: 10, size: "small" } : false}
          columns={[
            {
              title: "지급일", dataIndex: "payment_date", width: 100,
              render: (v: string, r: DividendResponse) => (
                <div>
                  <div style={{ fontSize: 12 }}>{v?.slice(0, 10)}</div>
                  {!screens.md && <AccountTag account={`${r.brokerage} ${r.account_type}`} />}
                </div>
              ),
            },
            {
              title: "계좌", dataIndex: "brokerage", responsive: ["md"] as const, width: 120,
              render: (v: string, r: DividendResponse) => <AccountTag brokerage={v} account={`${v} ${r.account_type}`} />,
            },
            { title: "1주당", dataIndex: "dividend_per_share", align: "right" as const, width: 80, render: (v: number) => <span style={{ fontSize: 12 }}>{priceFmt(v, overseas)}</span> },
            { title: "수량", dataIndex: "quantity", align: "right" as const, responsive: ["sm"] as const, width: 70, render: (v: number) => v?.toLocaleString("ko-KR") },
            { title: "배당금", dataIndex: "dividends_krw", align: "right" as const, width: 105, render: (v: number) => currency(v) },
          ] as any}
        />
      </Card>
    </>
  );
};

export default StockHistoryCard;
